import React, { useEffect } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Box
} from '@material-ui/core';
import { Box as AWSUIBox, Container as AWSUIContainer } from '@amzn/awsui-components-react-v3';
import { websiteViewTheme } from './styles/theme';
import { useAppLayoutStyles } from './styles/app-layout.styles';
import './styles/page-shell.scss';
import { WebsiteSelector } from './selector';
import { PrimitiveMetrics } from './primitive-metrics';
import { TimelineView } from './timeline-view';
import { WebsiteSummary } from './website-summary';
import { WebsiteHeader } from './website-header';
import { FunnelPanel } from '../shared-panels/FunnelPanel';
import { SlaPanel } from '../shared-panels/sla/SlaPanel';
import { metricFeaturesList, metricPageNames } from 'src/constants/swo-ui-metric-constants';
import { SwoUiMetrics } from 'src/utils/swo-ui-metrics/swo-ui-metrics';
import { UrlHelper } from '../../utils/url-helper';

export const WebsiteViewLayout = React.memo(() => {
    const classes = useAppLayoutStyles();
    const websiteId = UrlHelper.getParamValue('websiteId');
    // The funnel's status badge reads primitives, which are keyed on the domain, not the numeric id.
    const website = UrlHelper.getParamValue('website');

    useEffect(() => {
        SwoUiMetrics.publishCloudWatchCountMetric(metricPageNames.websiteView, metricFeaturesList.websiteAnalysisAndDiagnosticTool);
    }, [])

    return (
        <ThemeProvider theme={websiteViewTheme}>
            <CssBaseline />
            {/* The selector shares the page's centred column so it lines up with the panels below it,
                rather than running the full width above a 1180px column. */}
            <Box className="ovp-page">
                <Box className="ovp-page__wrap">
                    <AWSUIContainer header={
                        <AWSUIBox variant="h2" padding={{ top: "m" }}>
                        Website Flow Analysis & Diagnostic Tool
                    </AWSUIBox>
                    }>
                        <WebsiteSelector />
                    </AWSUIContainer>

                    {/* Main Content */}
                    <Box
                        component="main"
                        className={classes.mainContent}
                    >
                        {/* Who this page is about, so it leads: domain, status and every metadata field the
                            page holds. It replaces the EWC metadata card that used to sit below the timeline
                            — a superset of that card's eight fields, in one line rather than a full-width
                            twelve-cell grid. Unguarded because it guards itself: with nothing selected it
                            renders nothing, rather than an empty heading implying a lost website. */}
                        <WebsiteHeader />

                        <Box className={classes.cardContainer}>
                            <WebsiteSummary />
                        </Box>

                        {/* Timeline Section - Full Width */}
                        <Box className={`${classes.timelineSection} ovp-page__mui-panel`}>
                            <TimelineView />
                        </Box>

                        {/* Still conditional on a website after the release flag went away, and for a
                            different reason: the backend rejects a WEBSITE-scoped request that omits
                            websiteId rather than aggregating every website, so with none selected there is
                            nothing to ask it for. The funnel first and the SLA panel below it, deliberately:
                            the funnel answers how much work got through, and this answers whether what did
                            get through was on time. The second question only makes sense after the first. */}
                        {websiteId && (
                            <>
                                <Box className={classes.cardContainer}>
                                    <FunnelPanel
                                        scope={{ type: 'WEBSITE', websiteId: websiteId }}
                                        domain={website ?? undefined}
                                    />
                                </Box>
                                <Box className={classes.cardContainer}>
                                    <SlaPanel scope={{ type: 'WEBSITE', websiteId: websiteId }} />
                                </Box>
                            </>
                        )}

                        {/* Primitive Metrics Section - Full Width */}
                        <Box className={classes.cardContainer}>
                            <PrimitiveMetrics />
                        </Box>

                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
});

WebsiteViewLayout.displayName = 'WebsiteViewLayout';
